/**
 * Detach all media elements (pause + clear srcObject).
 */
export function forceDetachMediaElements({ localVideoRef, remoteVideoRef, remoteAudioRef }) {
  [localVideoRef?.current, remoteVideoRef?.current, remoteAudioRef?.current].forEach((mediaEl) => {
    if (!mediaEl) return;
    try {
      mediaEl.pause?.();
    } catch (_) { }
    try {
      mediaEl.srcObject = null;
    } catch (_) { }
  });
}

/**
 * Stop every track on a MediaStream (best-effort for teardown).
 */
export function forceStopStreamTracks(stream) {
  if (!stream) return;
  const tracks = stream.getTracks?.() || [];
  tracks.forEach((track) => {
    if (!track) return;
    try {
      track.enabled = false;
    } catch (_) { }
    try {
      track.stop?.();
    } catch (_) { }
  });
}

const GLOBAL_PLAYBACK_UNLOCK_EVENTS = ['pointerdown', 'touchstart', 'keydown', 'visibilitychange'];
let globalPlaybackUnlockAttached = false;
const playbackUnlockSubscribers = new Set();

const notifyPlaybackUnlockSubscribers = () => {
  playbackUnlockSubscribers.forEach((handler) => {
    try {
      handler();
    } catch (_) { }
  });
};

const ensureGlobalPlaybackUnlockListeners = () => {
  if (globalPlaybackUnlockAttached || typeof document === 'undefined') return;
  globalPlaybackUnlockAttached = true;
  GLOBAL_PLAYBACK_UNLOCK_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, notifyPlaybackUnlockSubscribers, true);
  });
};

const subscribeGlobalPlaybackUnlock = (handler) => {
  if (typeof handler !== 'function') return () => { };
  ensureGlobalPlaybackUnlockListeners();
  playbackUnlockSubscribers.add(handler);
  return () => {
    playbackUnlockSubscribers.delete(handler);
  };
};

/**
 * Returns attachStream bound to a WeakMap that tracks per-element track signatures
 * so camera swaps / wrapper-only stream changes do not freeze preview.
 */
export function createAttachStream(mediaElementTrackSignatureRef) {
  const mediaElementPlayCleanupRef = new WeakMap();
  const clearPlayRetryHandlers = (mediaEl) => {
    const cleanup = mediaElementPlayCleanupRef.get(mediaEl);
    if (typeof cleanup === 'function') cleanup();
    mediaElementPlayCleanupRef.delete(mediaEl);
  };

  const ensurePlayback = (mediaEl) => {
    if (!mediaEl) return;
    clearPlayRetryHandlers(mediaEl);
    let disposed = false;

    const tryPlay = () => {
      if (disposed || !mediaEl?.srcObject) return;
      const playPromise = mediaEl.play?.();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => { });
      }
    };

    const onGestureOrReady = () => {
      tryPlay();
    };

    mediaEl.addEventListener('loadedmetadata', onGestureOrReady);
    mediaEl.addEventListener('canplay', onGestureOrReady);
    mediaEl.addEventListener('playing', onGestureOrReady);

    const unsubscribeGlobalPlaybackUnlock = subscribeGlobalPlaybackUnlock(onGestureOrReady);

    mediaElementPlayCleanupRef.set(mediaEl, () => {
      disposed = true;
      mediaEl.removeEventListener('loadedmetadata', onGestureOrReady);
      mediaEl.removeEventListener('canplay', onGestureOrReady);
      mediaEl.removeEventListener('playing', onGestureOrReady);
      unsubscribeGlobalPlaybackUnlock();
    });

    tryPlay();
  };

  return function attachStream(
    mediaEl,
    stream,
    callEnded = false,
    trackKinds = ['video', 'audio']
  ) {
    if (!mediaEl) return;
    const trackKindsSet = new Set(trackKinds);
    const getTrackSignature = (s) =>
      (s?.getTracks?.() ?? [])
        .filter((t) => trackKindsSet.has(t.kind))
        .sort((a, b) => `${a.kind}:${a.id}`.localeCompare(`${b.kind}:${b.id}`))
        .map((t) => `${t.kind}:${t.id}`)
        .join(',');
    if (stream) {
      const prevStream = mediaEl.srcObject;
      const nextSignature = getTrackSignature(stream);
      const prevSignature = mediaElementTrackSignatureRef.current.get(mediaEl) ?? '';
      const streamChanged = prevStream !== stream;
      const tracksRotated = !streamChanged && prevSignature !== nextSignature;
      const streamWrapperOnly =
        streamChanged &&
        prevSignature === nextSignature &&
        prevSignature !== '';
      if (streamWrapperOnly) {
        if (mediaEl.srcObject !== stream) {
          mediaEl.srcObject = stream;
        }
        mediaElementTrackSignatureRef.current.set(mediaEl, nextSignature);
        ensurePlayback(mediaEl);
        return;
      }
      if (tracksRotated) {
        try {
          mediaEl.srcObject = null;
        } catch (_) { /* noop */ }
        mediaEl.srcObject = stream;
      } else if (streamChanged) {
        if (mediaEl.srcObject !== stream) {
          mediaEl.srcObject = stream;
        }
      }
      mediaElementTrackSignatureRef.current.set(mediaEl, nextSignature);
      const needsPlayback = streamChanged || tracksRotated || streamWrapperOnly;
      if (needsPlayback) {
        ensurePlayback(mediaEl);
      }
    } else if (callEnded) {
      mediaElementTrackSignatureRef.current.delete(mediaEl);
      clearPlayRetryHandlers(mediaEl);
      try {
        mediaEl.pause?.();
      } catch (_) { /* noop */ }
      mediaEl.srcObject = null;
    }
  };
}
